import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";

const COUNTRY_NAMES = {
  US: "United States", PH: "Philippines", CA: "Canada", GB: "United Kingdom",
  AU: "Australia", DE: "Germany", IN: "India", SG: "Singapore", JP: "Japan",
  KR: "South Korea", MX: "Mexico", NZ: "New Zealand", IE: "Ireland", FR: "France",
  NL: "Netherlands", ES: "Spain", IT: "Italy", SE: "Sweden", BR: "Brazil", AE: "UAE",
  SA: "Saudi Arabia", VN: "Vietnam", TH: "Thailand", MY: "Malaysia", ID: "Indonesia",
  Unknown: "Unknown",
};

function BarChart({ series }) {
  const max = Math.max(...series.map((s) => s.views), 1);
  return (
    <div className="admin-chart">
      {series.map((s) => (
        <div className="admin-chart-col" key={s.date} title={`${s.date}: ${s.views} views`}>
          <div className="admin-bar-track">
            <div className="admin-bar" style={{ height: `${Math.max((s.views / max) * 100, s.views ? 4 : 0)}%` }} />
          </div>
          <span className="admin-chart-label">{s.date.slice(2)}</span>
        </div>
      ))}
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="admin-stat">
      <span className="admin-stat-value">{typeof value === "number" ? value.toLocaleString() : value}</span>
      <span className="admin-stat-label">{label}</span>
      {sub && <span className="admin-stat-sub">{sub}</span>}
    </div>
  );
}

function Table({ rows, format }) {
  if (!rows.length) return <p className="admin-empty">No data yet</p>;
  const max = Number(rows[0][1]) || 1;
  return (
    <table className="admin-table">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}>
            <td className="admin-td-key">{format ? format(k) : k}</td>
            <td className="admin-td-bar">
              <span className="admin-td-fill" style={{ width: `${(Number(v) / max) * 100}%` }} />
            </td>
            <td className="admin-td-val">{Number(v).toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LoginForm({ onSuccess, expired }) {
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password: pw }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setErr(data.error || "Wrong password");
        return;
      }
      onSuccess();
    } catch {
      setErr("Network error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="admin-login" onSubmit={submit}>
      <h1>Analytics</h1>
      {expired && <p className="admin-error">Session expired — sign in again.</p>}
      <label htmlFor="admin-pw">Password</label>
      <div className="admin-pw-wrap">
        <input
          id="admin-pw"
          type={show ? "text" : "password"}
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="••••••••"
          autoFocus
          aria-label="Password"
        />
        <button
          type="button"
          className="admin-pw-toggle"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? "Hide password" : "Show password"}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </button>
      </div>
      {err && <p className="admin-error">{err}</p>}
      <button type="submit" disabled={!pw || busy}>
        {busy ? "Signing in…" : "Sign in"}
      </button>
    </form>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [unauth, setUnauth] = useState(false);
  const [expired, setExpired] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/stats", { credentials: "include" });
      if (res.status === 401) {
        setUnauth(true);
        setStats(null);
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || "Could not load stats");
        return;
      }
      setStats(await res.json());
      setUnauth(false);
    } catch {
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try {
      await fetch("/api/admin/logout", { method: "POST", credentials: "include" });
    } catch { /* ignore */ }
    setStats(null);
    setUnauth(true);
  };

  return (
    <div id="posts">
      <Helmet>
        <title>Analytics | charlie-gp</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {unauth && !stats ? (
        <LoginForm
          onSuccess={() => {
            setUnauth(false);
            setExpired(false);
            load();
          }}
          expired={expired}
        />
      ) : (
        <div className="admin-wrap">
          <header className="admin-header">
            <h1>charliegp Analytics</h1>
            <div className="admin-header-actions">
              <button className="admin-view" onClick={load}>Refresh</button>
              <a className="admin-view" href="/" target="_blank" rel="noopener noreferrer">View site</a>
              <button className="admin-logout" onClick={logout}>Sign out</button>
            </div>
          </header>

          {loading ? (
            <p className="admin-empty">Loading…</p>
          ) : error ? (
            <div className="admin-error-box">
              <h2>Analytics unavailable</h2>
              <p>{error}</p>
            </div>
          ) : stats ? (
            <>
              <section className="admin-stats">
                <StatCard label="Total views" value={stats.totalViews} />
                <StatCard label="Unique visitors" value={stats.uniqueVisitors} />
                <StatCard label="Views today" value={stats.todayViews} />
                <StatCard label="Uniques today" value={stats.todayUniques} />
              </section>

              <section className="admin-panel">
                <h2>Last 30 days</h2>
                <BarChart series={stats.series} />
              </section>

              <section className="admin-grid">
                <div className="admin-panel">
                  <h2>Top pages</h2>
                  <Table rows={stats.topPages} format={(k) => (k === "/" ? "/ (Home)" : k)} />
                </div>
                <div className="admin-panel">
                  <h2>Top pages today</h2>
                  <Table rows={stats.topPagesToday} format={(k) => (k === "/" ? "/ (Home)" : k)} />
                </div>
                <div className="admin-panel">
                  <h2>Devices</h2>
                  <Table rows={stats.devices} />
                </div>
                <div className="admin-panel">
                  <h2>Referrers</h2>
                  <Table rows={stats.referrers} />
                </div>
                <div className="admin-panel">
                  <h2>Countries</h2>
                  <Table rows={stats.countries} format={(k) => COUNTRY_NAMES[k] || k} />
                </div>
                <div className="admin-panel">
                  <h2>Browsers</h2>
                  <Table rows={stats.browsers} />
                </div>
                <div className="admin-panel">
                  <h2>Operating systems</h2>
                  <Table rows={stats.os} />
                </div>
              </section>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}